import {useCallback, useEffect, useState} from 'react';
import type {Order, OrderLine, PrepState} from '../../api/types';
import {useRouter} from '../../app/router';
import {ROUTES, href} from '../../app/routes';
import {reasonMessageKey, useI18n, type MessageKey} from '../../i18n';
import {useMenu} from '../../menu';
import {useOrder} from '../../order';
import {useGuestSession} from '../../session';
import {useGuestStream, useStreamEvents} from '../../stream';
import {
  ActionLink,
  Button,
  Card,
  Screen,
  ScreenBody,
  ScreenFooter,
  SkeletonBar,
  SkeletonRow,
  TopBar,
  cx,
} from '../../ui';
import {LanguageToggle} from '../Q02LanguageToggle';
import {PaymentReturnNotice} from '../Q09PaymentVerification';
import './q14.css';

/**
 * # Q14 · Order status, dine-in — route `/status`
 *
 * The screen a guest opens *instead of* waving at a server. Its entire value is
 * that it is believed, so every decision below is made in favour of honesty over
 * reassurance.
 *
 * ## What is on it, and what is deliberately not
 *
 * The spec's data list for Q14 is "received → cooking → ready, per-item where
 * meaningful, elapsed". That is what is here.
 *
 *   * **No money figures at all.** Not the total, not the line prices. The bill
 *     is Q17's job and this order is unpaid by definition — a dine-in ticket
 *     settles at the counter. A number on this screen would be a total nobody
 *     asked for, in the one place the guest is not deciding about money. The
 *     design package draws the same screen the same way and says so: *no price
 *     at all on the status row, deliberate, this screen is about food, not
 *     money.* Consequently the money rule is satisfied structurally: there is
 *     nothing here that *could* be Pandan. `q14.billLater` says how payment
 *     happens, in words, with no amount.
 *   * **No images.** Not "no photo as a fallback" — this screen renders no
 *     `image_url` on any path. A status list is read in seconds while food is
 *     coming; spending 4G on pictures of dishes the guest already chose is the
 *     clearest waste in the perf budget, and photo coverage is a TBD anyway.
 *   * **No hex literal and no local re-invention of a shared shape.** Layout,
 *     surface, type and the tap floor come from `../../ui`; the rules at the
 *     bottom of this file are only what the shared layer has no opinion about —
 *     the three-column status row and the de-emphasis of a stale rail. The
 *     screen used to carry its own shell, buttons, card and type scale, which is
 *     thirteen chances to disagree about a hairline.
 *
 * ## The rail is an extrapolation, and it is not green
 *
 * The design package **does not draw a status rail.** It names four steps in a
 * constant and then renders the first one, hard-coded, as the right-hand label
 * of every row. `StatusRail` is built from the vocabulary the package *does*
 * draw — page-ground rows, the warm hairline, state carried by weight and colour
 * rather than by size — and its "done" colour is Butterfly Pea. "Ready to serve"
 * is a prep state, not verified money; Pandan here would teach a guest that
 * green means "done" three screens before it means "paid".
 *
 * ## Honesty, concretely
 *
 *   * When the stream is not `open` the progress block is **visibly
 *     de-emphasised and labelled stale** (`q14.staleTitle` / `q14.staleBody`)
 *     rather than frozen mid-animation. A dead progress bar is a lie a guest
 *     waits on. The de-emphasis is a dashed edge and River Stone on the
 *     headline — colour and weight, never `opacity`, which on a sun-washed
 *     panel takes body copy below the contrast it needs to be read at all.
 *   * `common.updatedAt` is always shown, so "when was this true" never has to
 *     be inferred from whether something is moving. It renders through
 *     `useI18n().time`, which formats in the **venue's** timezone: a guest whose
 *     phone is still on Europe/London must not read a Bangkok kitchen's clock
 *     seven hours out.
 *   * Elapsed is computed against the **server** clock: stream frames carry
 *     `occurred_at`, which is a live server-now probe, and the difference from
 *     `Date.now()` at the moment a frame lands is this device's skew. With no
 *     probe yet, an implausible elapsed is hidden rather than shown wrong.
 *   * Nothing animates. Not the rail, not the skeleton, not the arrival of a new
 *     state. This screen is re-read every ninety seconds by someone who is
 *     hungry, and motion on it would read as progress it cannot vouch for.
 *   * Nothing carries meaning by colour alone: every state has a written label.
 *     "Ready" is emphasised with ink and weight, which is also the only emphasis
 *     that survives a monochrome or sun-washed panel.
 *
 * ## A dish that vanishes
 *
 * Three different disappearances, three visible treatments, none of them silent:
 *
 *   1. A **voided or cancelled line stays on screen**, struck through and
 *      labelled `q14.stateCancelled`. It is never filtered out — a guest who
 *      counts four dishes and receives three must be able to see which one went.
 *   2. A **whole order voided** by the venue gets `q14.orderVoided` at the top
 *      and the progress block disappears entirely (there is no progress).
 *   3. A line the **kitchen has not started** (`pending`) whose item is now
 *      unavailable in the live projection gets an advisory with the venue's own
 *      reason (`reason.*`) and `common.askStaff`. Deliberately only `pending`:
 *      an 86 means "no more portions for new orders", so once a dish is `fired`
 *      or `in_progress` the flag says nothing about *this* plate, and saying it
 *      does would be the lie this screen exists to avoid.
 *
 * Those three notices are Palm Sugar, not Dried Chili. A venue taking a dish off
 * the ticket is something the guest needs to notice and act on; it is not an
 * error, and it is certainly not the guest's. Dried Chili on this surface is the
 * outline of a **destructive control**, and there is no destructive control here.
 */

/** Kitchen order of `PrepState`. The index is the progress position. */
const PREP_ORDER: readonly PrepState[] = [
  'pending',
  'fired',
  'in_progress',
  'ready',
  'served',
];

const PREP_LABEL: Record<PrepState, MessageKey> = {
  pending: 'q14.statePending',
  fired: 'q14.stateFired',
  in_progress: 'q14.stateInProgress',
  ready: 'q14.stateReady',
  served: 'q14.stateServed',
  cancelled: 'q14.stateCancelled',
};


/** A line still counts toward progress unless the venue took it off the ticket. */
function isActive(line: OrderLine): boolean {
  return !line.voided && line.prep_state !== 'cancelled';
}

/**
 * Merge lines the guest cannot tell apart, and sum their counts.
 *
 * A second round of the same dish arrives as its own `line_item_added`, so a
 * guest who orders ข้าวผัดหมู in two rounds gets two rows — and on a list read at
 * a glance while counting plates, "1 ข้าวผัดหมู" above "2 ข้าวผัดหมู" reads as
 * two different things rather than three of one (owner, 2026-08-18).
 *
 * The grouping key is everything that would make two lines *different to the
 * person waiting for the food*: the dish, its modifiers, its kitchen note, and —
 * the two that keep this honest — its **kitchen state** and whether it is still
 * on the ticket. A plate already `พร้อมเสิร์ฟ` must not be folded into others
 * still `ครัวรับแล้ว`, and a struck-through cancelled one must not be folded into
 * a live one; merging across those would hide exactly what this screen exists to
 * show. So `ข้าวมันไก่` with a `เผ็ดๆ` note, or one dish that is ready while its
 * twins are still cooking, each stay their own row.
 *
 * Order is first-appearance, so the list does not reshuffle as rounds land.
 */
function mergeIdenticalLines(lines: readonly OrderLine[]): {key: string; line: OrderLine}[] {
  const groups = new Map<string, OrderLine>();
  for (const line of lines) {
    const modifierSignature = (line.modifiers ?? [])
      .map((modifier) => `${modifier.modifier_id}×${modifier.quantity}`)
      .sort()
      .join(',');
    const key = [
      line.item_id,
      // A voided line and a `cancelled` one are both "off the ticket", but the
      // reasons differ, so key on `voided` explicitly and let `prep_state` carry
      // the rest — never collapsing an active line into a gone one.
      line.voided ? 'voided' : line.prep_state,
      line.note ?? '',
      modifierSignature,
    ].join('|');
    const existing = groups.get(key);
    if (existing) {
      // The map holds a shallow copy, so summing here never mutates the order
      // held in the store.
      existing.quantity += line.quantity;
    } else {
      groups.set(key, {...line});
    }
  }
  return [...groups.entries()].map(([key, line]) => ({key, line}));
}

/**
 * The order's position is its **least advanced** line: the table is not waiting
 * on the fastest dish. Null when every line is off the ticket.
 */
function aggregatePrepState(order: Order): PrepState | null {
  let lowest = Number.POSITIVE_INFINITY;
  for (const line of order.lines) {
    if (!isActive(line)) continue;
    const index = PREP_ORDER.indexOf(line.prep_state);
    if (index >= 0 && index < lowest) lowest = index;
  }
  return Number.isFinite(lowest) ? PREP_ORDER[lowest]! : null;
}


/**
 * How long the stream may be un-`open` before the guest is told.
 *
 * The order on screen was just fetched, and an SSE connection over 4G routinely
 * takes a second or two to establish — so an instant "this may be out of date"
 * on every entry would be both untrue and, worse, a warning a guest learns to
 * ignore. It has to mean something by the time it appears.
 */
const STALE_GRACE_MS = 5_000;

export default function Q14OrderStatus() {
  const {t, ln} = useI18n();
  const {navigate} = useRouter();
  const {status: sessionStatus} = useGuestSession();
  const {
    order: liveOrder,
    refresh,
    loadOrder,
    knownOrderIds,
    reset,
    callStaff,
    requestBill,
  } = useOrder();
  const {stale, resetNonce} = useGuestStream();
  const {availabilityOf} = useMenu();

  /**
   * A reload of `/status` is the ordinary case — the guest closed the tab, or the
   * phone dropped it under memory pressure — and `OrderProvider` only re-attaches
   * from `session.active_order_id`. When the table's ticket has already closed,
   * or the session response has not caught up, the last order this phone
   * submitted is still the right thing to show. "No order yet" while food is on
   * its way is the single worst thing this screen can say.
   */
  const [recovered, setRecovered] = useState<Order | null>(null);
  const recoverableId = liveOrder ? null : (knownOrderIds[knownOrderIds.length - 1] ?? null);

  /**
   * Whether that recovery has finished, either way.
   *
   * It exists only so the screen can tell "still fetching" from "there is
   * nothing" — see `booting` below. It is deliberately a settle flag rather than
   * an in-flight flag: a fetch that fails must fall through to the empty state
   * and stay there, not leave a skeleton on the page for the rest of the meal.
   */
  const [recoverySettled, setRecoverySettled] = useState(false);

  const reloadRecovered = useCallback(
    (orderId: string) => {
      loadOrder(orderId)
        .then((loaded) => {
          setRecovered(loaded);
          setRecoverySettled(true);
        })
        .catch(() => {
          // Offline, or the ticket is gone. Keep whatever is on screen; the
          // stale notice already tells the truth about why it is not moving.
          setRecoverySettled(true);
        });
    },
    [loadOrder],
  );

  useEffect(() => {
    if (!recoverableId || sessionStatus !== 'active') return;
    reloadRecovered(recoverableId);
    // `resetNonce` re-reads after a stream reset, when every snapshot is suspect.
  }, [recoverableId, sessionStatus, reloadRecovered, resetNonce]);

  const order = liveOrder ?? recovered;

  /*
   * This handler used to borrow the server's clock as well — every frame carries
   * `occurred_at`, so the gap to `Date.now()` when it lands is this device's
   * skew, which is what kept "20 minutes ago" a fact about the kitchen rather
   * than about a wrong phone. It went with the progress block that displayed it
   * (owner directive, 2026-08-14). Restoring it is four lines and a ref; do that
   * before printing any elapsed time here again, because the reason it existed
   * has not changed.
   */
  useStreamEvents((event) => {
    // A frame about the recovered order is a hint to re-read it. The provider
    // already does this for the order it owns; it does not know about this one.
    if (!liveOrder && recovered && event.order_id === recovered.order_id) {
      const seq = event.order_seq;
      if (seq === null || seq === undefined || seq > recovered.seq) {
        reloadRecovered(recovered.order_id);
      }
    }
  });

  const [staleShown, setStaleShown] = useState(false);
  useEffect(() => {
    if (!stale) {
      setStaleShown(false);
      return;
    }
    const id = setTimeout(() => setStaleShown(true), STALE_GRACE_MS);
    return () => clearTimeout(id);
  }, [stale]);

  /*
   * `elapsedMinutes` and `updatedAt` were computed here, and a 30-second timer
   * re-rendered the screen so the first of them stayed true. All three went with
   * the progress block that showed them (owner directive, 2026-08-14). The
   * screen no longer re-renders on a clock at all — it updates when the stream
   * says something changed, which is the only thing it now displays.
   */

  const retry = useCallback(() => {
    if (liveOrder) void refresh().catch(() => undefined);
    else if (recovered) reloadRecovered(recovered.order_id);
    else if (recoverableId) reloadRecovered(recoverableId);
  }, [liveOrder, refresh, recovered, recoverableId, reloadRecovered]);

  /*
    The two asks, and why they are on this screen.

    Until 2026-08-16 this surface could tell a guest what the kitchen was doing
    and gave them no way to say anything back — so the only channel left was
    standing up and waving, which is the thing a QR surface exists to remove
    (owner report, against `order-staging`).

    **What is claimed after a press is exactly what the server knows.** The call
    was recorded; nobody can promise from here that a person is walking over. So
    the button becomes a state rather than a receipt, and a second press is
    neither an error nor a second call — the server keeps the first unanswered
    one, so the floor sorts by who has waited longest.
  */
  const [asking, setAsking] = useState<'staff' | 'bill' | null>(null);
  const [askFailed, setAskFailed] = useState(false);

  // **Read off the order, not off a local flag.** The state belongs to the
  // table, not to this phone: a second guest on the same QR must see that
  // somebody has already called, and a reload must not offer to call again as
  // though the first press had never happened.
  const staffCalled = Boolean(order?.staff_call_open_since);
  const billRequested = order?.state === 'awaiting_payment';

  const ask = useCallback(
    async (which: 'staff' | 'bill') => {
      setAskFailed(false);
      setAsking(which);
      try {
        await (which === 'staff' ? callStaff() : requestBill());
      } catch {
        // The message is the same for a dropped connection and a refusal,
        // because the guest's move is the same: press it again, or catch
        // somebody's eye. Nothing here shows an error code to a person waiting
        // for lunch.
        setAskFailed(true);
      } finally {
        setAsking(null);
      }
    },
    [callStaff, requestBill],
  );

  /** A second round starts from the menu with a fresh submission identity. */
  const orderAgain = useCallback(() => {
    reset();
    navigate(ROUTES.menu);
  }, [reset, navigate]);

  /*
    The language control lives in the top bar on every branch below, including
    the two that show no order: foundations §8, and a guest waiting on food is
    exactly the guest who has time to notice they cannot read the page.

    There is deliberately **no back link in the leading slot**, and that is a
    departure from the design package, which draws a `common.back` link here.
    See the footer for the argument.
  */
  const topBar = <TopBar trailing={<LanguageToggle variant="link" />} />;

  /*
    The three rules at the bottom of this file that `../../ui` has no opinion
    about. `Screen` already mounts the shared stylesheet; this is the remainder,
    and it is inline for the same reason `uiCss.ts` is a string — an imported
    `.css` file does not exist under vitest, and a geometry that cannot be
    asserted is a geometry that drifts.
  */
  const styles = <style dangerouslySetInnerHTML={{__html: CSS}} />;

  // --- still fetching -------------------------------------------------------

  /**
   * The gap between opening `/status` and the ticket arriving.
   *
   * The design package has no loading state anywhere in 485 lines, so this is
   * invented — from `SkeletonBar` at the geometry of the real rows, so nothing
   * reflows when the order lands, and static rather than shimmering, because an
   * animation costs frames on the phone least able to spare them.
   *
   * It is bounded by construction: `recoverySettled` flips on both the success
   * and the failure path, and `opening` is a session state the provider always
   * leaves. A skeleton that can outlive its fetch is worse than the empty state
   * it was hiding.
   */
  const booting =
    !order && (sessionStatus === 'opening' || (recoverableId !== null && !recoverySettled));

  if (booting) {
    return (
      <Screen>
        {styles}
        {topBar}
        <ScreenBody className="q14">
          <h1 className="pk-title">{t('q14.title')}</h1>
          <div role="status" aria-busy="true" aria-label={t('common.loading')}>
            {[0, 1, 2].map((row) => (
              <SkeletonRow key={row}>
                <SkeletonBar width="24px" />
                <SkeletonBar />
                <SkeletonBar width="96px" />
              </SkeletonRow>
            ))}
          </div>
        </ScreenBody>
      </Screen>
    );
  }

  // --- nothing to show ------------------------------------------------------

  if (!order) {
    const sessionGone = sessionStatus === 'expired' || sessionStatus === 'invalid';
    return (
      <Screen>
        {styles}
        {topBar}
        {/*
          The centred short-message layout, borrowed from the success screen —
          the one shape the package draws for "this is the whole page". No ring
          and no glyph: `TerminalNotice` marks an outcome, and having no order
          yet is not an outcome, it is an absence.
        */}
        <ScreenBody center>
          <h1 className="pk-title">{t('q14.title')}</h1>
          <p className="pk-muted">{sessionGone ? t('error.session') : t('q14.noOrder')}</p>
          {sessionGone ? <p className="pk-hint">{t('common.askStaff')}</p> : null}
        </ScreenBody>
        <ScreenFooter bordered={false}>
          {sessionGone ? null : (
            <ActionLink to={ROUTES.menu} variant="primary">
              {t('q14.noOrderAction')}
            </ActionLink>
          )}
          <p className="pk-hint q14-attribution">{t('common.attribution')}</p>
        </ScreenFooter>
      </Screen>
    );
  }

  // --- the order ------------------------------------------------------------

  const aggregate = aggregatePrepState(order);
  const voided = order.state === 'voided';
  /** Every line taken off the ticket, but the order itself was not voided. */
  const allLinesGone = !voided && aggregate === null && order.lines.length > 0;
  return (
    <Screen>
      {styles}
      {topBar}

      {/*
        `scroll` because this screen has a pinned footer, and the footer is
        pinned by flex rather than by `position: fixed` — a fixed bar on a mobile
        browser fights the collapsing URL bar, and this is a guest's own phone.
      */}
      <ScreenBody scroll className="q14">
        <div className="pk-stack">
          <h1 className="pk-title">{t('q14.title')}</h1>
          <p className="pk-muted">{t('q14.received')}</p>
        </div>

        {/*
          INTEGRATION · Q9's notice, on Q14's screen, because ReservePay sends a
          returning payer HERE. `POKPOK_GUEST_RETURN_URL` is
          `https://<guest host>/status` — set in `pokpok-infra`, not decidable
          from this repository — so the guest who has just handed over money
          arrives on the food-status list, which knows nothing about a payment.
          Without this line their payment has no acknowledgement anywhere.

          It renders `null` unless this phone has an open checkout attempt, so it
          costs one `localStorage` read and adds no chrome to the ordinary case.
          The component lives in Q9 and is used here, rather than being
          reproduced here, for the same reason there is exactly one
          `LanguageToggle`.

          **This claims nothing about the money.** It says a payment is waiting
          to be confirmed and offers the screen that explains it — see Q9's
          header for why "confirmed" is a longer story than it should be.
        */}
        <PaymentReturnNotice />

        {voided && (
          <Card tone="warning" role="status" className="pk-stack">
            <span className="pk-strong">{t('q14.orderVoided')}</span>
            <span className="pk-muted">{t('common.askStaff')}</span>
          </Card>
        )}
        {allLinesGone && (
          <Card tone="warning" role="status" className="pk-stack">
            <span className="pk-strong">{t('q14.orderCancelled')}</span>
            <span className="pk-muted">{t('common.askStaff')}</span>
          </Card>
        )}

        {/*
          Offline is the supported state of this product, not a fault
          (foundations §6) — so this is Palm Sugar and a plain sentence, never an
          error. What it must not do is let the rail below keep looking live.

          `tertiary` on the retry, not `primary`: the one solid red on this
          screen is spent below, and a second one here would make two forward
          actions out of a screen that has one.
        */}
        {staleShown && (
          <Card tone="warning" aria-live="polite" className="pk-stack q14-stale">
            <span className="pk-strong">{t('q14.staleTitle')}</span>
            <span className="pk-muted">{t('q14.staleBody')}</span>
            <Button variant="tertiary" onClick={retry}>
              {t('common.retry')}
            </Button>
          </Card>
        )}

        {/*
          THE PROGRESS BLOCK IS GONE — owner directive, 2026-08-14.

          It was the aggregate state as a headline, the elapsed/updated meta
          line, and the four-step rail. All of it: not the card around it, the
          block itself. The per-item list below now carries the whole answer,
          which is the more honest place for it — the order's position was
          computed as its *least advanced* line, so the headline was a summary of
          the list restated one line above the list.

          `StatusRail` stays in `src/ui` and is used by nothing on this surface.
          It is the one piece of this screen the design package does not draw
          (the prototype computes `steps` and `stepLabels` and never renders
          them), so if the aggregate comes back, it comes back to a component
          that already exists.
        */}

        <section>
          <h2 className="q14-heading">{t('q14.perItemHeading')}</h2>
          <ul className="q14-list">
            {mergeIdenticalLines(order.lines).map(({key, line}) => (
              <LineRow
                key={key}
                line={line}
                name={ln(line.name_snapshot)}
                modifiers={(line.modifiers ?? []).map((modifier) => ln(modifier.name_snapshot))}
                stateLabel={t(PREP_LABEL[line.voided ? 'cancelled' : line.prep_state])}
                gone={line.voided || line.prep_state === 'cancelled'}
                quantityLabel={t('common.quantity')}
                /*
                 * Only for a line the kitchen has not started. See the header: an
                 * 86 on a dish already firing says nothing about this plate.
                 */
                soldOut={
                  !line.voided &&
                  line.prep_state === 'pending' &&
                  availabilityOf(line.item_id)?.available === false
                    ? {
                        reason: (() => {
                          const reason = availabilityOf(line.item_id)?.reason;
                          const key = reasonMessageKey(reason);
                          return key ? t(key) : t('common.soldOut');
                        })(),
                        action: t('common.askStaff'),
                      }
                    : null
                }
              />
            ))}
          </ul>
        </section>

        {/*
          `q14.billLater` ("จ่ายทีหลังที่เคาน์เตอร์") removed by owner directive,
          2026-08-14. By the time a guest is on this screen the food is with the
          kitchen and the sentence has already been shown at the moment it was a
          decision — in the confirm sheet on the cart. Repeating it here tells
          someone waiting for lunch about a debt they have already agreed to.

          The summary link below is still the way to the bill.
        */}
      </ScreenBody>

      {/*
        No rule above this footer and no top padding: the design draws the
        bordered footer on the screens whose content scrolls *under* it and the
        plain one here, and it means both.

        Two decisions in these three lines.

        **The order carries the red, and it is on "order more".** That is the
        design package's choice — outline for the bill, solid for another round —
        and it is worth naming rather than inheriting silently, because the case
        against it is real: a guest on this screen is more often heading for the
        bill than for a second plate, and the red arguably belongs there. It is
        kept as drawn because this is a redesign and not a re-decision, and
        because flipping it is a product call that should be made once for the
        whole surface rather than by one screen. **Escalated, not settled.**

        **There is no back link on this screen.** The only destination one could
        have is the menu, and the menu is already reached by `q14.orderAgain`
        below, which also calls `reset()` — clearing the submission identity so the next
        round is a new order. A plain `<a>` cannot do that, so a back link here
        would be a second control to the same place with different consequences:
        a guest who took it would build a fresh cart and find Q7 still showing
        the *previous* order's success screen, because `submitStatus` would still
        be `sent`. Two doors, one of them quietly broken, is worse than one door.
        Q14 is reached by `replace` from the send confirmation anyway, so the
        browser's own back gesture already lands on the menu.
      */}
      <ScreenFooter bordered={false}>
        {/*
          The two asks sit above the existing pair rather than replacing either.
          `สรุปออเดอร์` is a read and `สั่งเพิ่ม` starts a round; these are the
          only controls on the surface that address a person.

          Neither is `primary`: the red on this screen belongs to "order more"
          (the design package's choice, argued below), and a call for staff is
          not a louder version of that — it is a different kind of act.
        */}
        <div className="q14-asks">
          <Button
            variant="secondary"
            onClick={() => void ask('staff')}
            disabled={asking !== null || staffCalled}
            disabledReason={
              staffCalled ? t('q14.callStaffPending') : t('common.loading')
            }
            reasonVisible={false}
          >
            {staffCalled ? t('q14.callStaffPending') : t('q14.callStaff')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => void ask('bill')}
            disabled={asking !== null || billRequested}
            disabledReason={
              billRequested ? t('q14.requestBillDone') : t('common.loading')
            }
            reasonVisible={false}
          >
            {billRequested ? t('q14.requestBillDone') : t('q14.requestBill')}
          </Button>
        </div>
        {(staffCalled || billRequested || askFailed) && (
          <p className="pk-hint q14-ask-note">
            {askFailed
              ? t('q14.askFailed')
              : staffCalled
                ? t('q14.callStaffSent')
                : t('q14.requestBillSent')}
          </p>
        )}
        <ActionLink
          to={href(ROUTES.receipt, {orderId: order.order_id})}
          variant="secondary"
          block
        >
          {t('q17.title')}
        </ActionLink>
        <Button variant="primary" onClick={orderAgain}>
          {t('q14.orderAgain')}
        </Button>
        <p className="pk-hint q14-attribution">{t('common.attribution')}</p>
      </ScreenFooter>
    </Screen>
  );
}

function LineRow({
  line,
  name,
  modifiers,
  stateLabel,
  gone,
  quantityLabel,
  soldOut,
}: {
  line: OrderLine;
  name: string;
  modifiers: string[];
  stateLabel: string;
  gone: boolean;
  quantityLabel: string;
  soldOut: {reason: string; action: string} | null;
}) {
  return (
    <li className={cx('pk-ruled', 'q14-line', gone && 'q14-line--gone')}>
      <span className="q14-qty">
        <span className="pokpok-sr-only">{quantityLabel}</span>
        {line.quantity}
      </span>
      <span className="q14-line-body">
        <span className="pk-strong q14-line-name">{name}</span>
        {modifiers.length > 0 && <span className="pk-muted">{modifiers.join(' · ')}</span>}
        {line.note ? <span className="pk-muted">{line.note}</span> : null}
        {soldOut && (
          <span className="q14-line-alert">
            {soldOut.reason} · {soldOut.action}
          </span>
        )}
      </span>
      {/*
        The label is the state. Colour never carries it (foundations §12), which
        is why this stays plain River Stone text in every state rather than
        becoming a pill on the two rows that are unusual — a column that changes
        shape row to row is read slower than one that does not, and this list is
        read at a glance by someone counting dishes.
      */}
      <span className="q14-line-state">{stateLabel}</span>
    </li>
  );
}

/**
 * What is left after the shared layer takes its share.
 *
 * Scoped to `q14-` because every screen shares one document, and **tokens only**
 * — no hard-coded brand colour anywhere below, so the palette stays the theme's
 * and a brand change in astryx lands here without anyone editing this file. (It
 * used to say the dark surface came free as well. The guest surface is light
 * only from 2026-08-13 — see `src/theme/ThemeStyles.tsx` — so that is no longer
 * a benefit this rule buys; the one it does buy is worth it on its own.)
 *
 * Almost everything this file used to declare — the shell, the buttons, the
 * card, the type scale, the tap floor — now comes from `../../ui`. What is left
 * is here because it is genuinely local:
 *
 *   1. **The three-column status row.** Quantity, dish, kitchen state. `pk-ruled`
 *      gives it the warm hairline and the vertical rhythm; the columns are this
 *      screen's shape and no other screen's. Including the weights: the outer two
 *      columns are 600 in River Stone and the dish is 600 in ink, which is what
 *      makes the middle column read as the content and the outer two as its
 *      frame. They are three separate declarations of one idea, so the rule for
 *      each says which half of it it carries.
 *   2. **The stale de-emphasis.** A dashed edge and a River Stone headline in
 *      place of ink. Not `opacity`, which fades the text and the border together
 *      and takes 16px body copy under its contrast floor on the surface this is
 *      most often read on — a phone at arm's length in a bright dining room.
 *   3. **Tabular figures on the elapsed line**, so `q14.elapsed` does not shuffle
 *      sideways when its `{minutes}` goes from one digit to two — which it does,
 *      on a screen re-read every ninety seconds, in front of the guest.
 *      `.pokpok-money` would do it and would also make the line bold and
 *      unwrappable, which it is not: this is not money.
 *   4. **The column gap between blocks**, one step, so nothing on this screen
 *      needs a margin of its own and no two neighbours can disagree about it.
 *
 * ## Why the progress block is a card when the design's status screen has none
 *
 * The package draws this screen entirely on the page ground: rows, hairlines,
 * nothing boxed. It also draws no rail, so the question does not come up there.
 * The three things the rail block holds — the current state, when it was last
 * true, and the four steps — are one assertion about one moment, and the card's
 * edge is what says so. It is also what the stale state has to modify: a dashed
 * hairline is a legible "provisional" and there is no way to dash a page ground.
 * The card is the flat one every other screen uses; nothing here has a shadow.
 *
 * No size below 16px, no line-height below 1.6, no shadow, no animation.
 */
const CSS = `
.q14 {
  display: flex;
  flex-direction: column;
  gap: var(--pokpok-space-4);
}

/* The rail's own headline: the one thing on the screen a guest reads from
   across the table. Kanit at the title step, via .pk-title. */
.q14-state {
  margin: 0;
}
.q14-meta {
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
}

/* Stale means the rail below is the last thing we heard, not the truth now.
   A dashed edge says "provisional" without touching the text's contrast, and
   the headline drops from ink to River Stone because it is no longer being
   asserted as a current fact. */
.q14-progress--stale {
  border-style: dashed;
}
.q14-progress--stale .q14-state {
  color: var(--color-text-secondary);
}

/* The stale card's retry sits under two lines of copy, not beside them: at 48px
   and full-width-when-block it would crowd them, and a warning the guest has not
   finished reading is a warning they will not act on. */
.q14-stale .pk-btn {
  align-self: flex-start;
  background: var(--color-background-card);
}

.q14-heading {
  font-size: var(--text-body-size);
  font-weight: 700;
  margin: 0 0 var(--pokpok-space-2);
}
.q14-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
/* Two departures from the design package's row, both load-bearing.

   **Top-aligned, where the package centres.** Its row is always exactly two
   lines tall — dish and meta — so centring the count and the state against that
   pair is unambiguous. This row is not: it can carry modifiers, a note and a
   sold-out advisory, and the state column is allowed to wrap (see below). Once
   the middle column can be four lines tall, centring floats the count into the
   middle of the modifier list, next to nothing it is the count *of*. Top-aligned
   puts all three columns' first lines on one optical line, which is the line a
   guest scans down.

   **16px between the columns, where the package uses 10.** 10px is not a step on
   this surface; the candidates are 8 and 16. 16 is chosen because the outer two
   columns now carry weight (below) — at 8px a 600-weight count sits close enough
   to a 600-weight dish name to read as one string, "2 phat kaphrao", which is
   the one thing the three-column layout exists to prevent. The cost is real and
   paid knowingly: ~16px less width for the dish name on a 390px frame.

   The example is romanised, and the Thai guard is the reason. That guard means
   to allow a comment that names a Thai string — Q17's receipt paragraph is one
   — and it does so by blanking JS comments while keeping string contents
   verbatim, because a shipping literal is caught precisely by surviving that
   pass. A CSS comment, this one included, sits inside the CSS template literal
   and is therefore string content: Thai written here is indexed as a shipping
   literal and fails. Romanised carries this particular argument unchanged;
   anything that needs the Thai itself belongs in the JS comment above the CSS
   constant, where the guard can see it is prose. (Backticks are unusable here
   for the same reason — one would end the literal.) */
.q14-line {
  display: flex;
  align-items: flex-start;
  gap: var(--pokpok-space-4);
}
/* A narrow column, quiet by colour and firm by weight — and the two are doing
   different jobs. River Stone is what keeps the count subordinate to the dish it
   counts; the 600 is what makes it read as a *column* rather than as a stray
   digit leaning on the name. The package sets 600 here and 600 on the state at
   the far edge, with the dish also at 600 but in ink: three columns, one weight,
   the middle one distinguished by colour. That is the whole mechanism by which
   the row parses at a glance, and dropping the weight from the outer two
   collapses all three into one grey texture. Tabular so a 1 and a 2 occupy the
   same width and the names below each other start on the same pixel. */
.q14-qty {
  flex: 0 0 auto;
  min-width: 24px;
  font-weight: 600;
  color: var(--color-text-secondary);
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
}
/* No gap declared, deliberately, where the package sets 2px between the dish
   and its meta line. At the package's sizes (14px name, 11px meta, tight
   leading) 2px is what stops the two touching. At this surface's floors — 16px
   body, 1.6 line-height — the half-leading already puts ~10px of air between
   them, so adding a step on top would separate the modifiers from the dish they
   belong to rather than merely un-crowd them. The floors beat the package where
   they collide, and this is a collision. */
.q14-line-body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}
.q14-line-alert {
  color: var(--color-warning);
}
/* The design drew this label at 11.5px, where it always fitted on one line. At
   the 16px floor it does not — "Waiting for the kitchen" is most of a phone's
   width — so it is allowed to shrink and wrap rather than push the dish name off
   the row. Capped at a third so it can never win the argument with the food.

   The 600 is the package's and it is the other half of the mechanism described
   on .q14-qty: this column and the count frame the dish between them at one
   shared weight, and the dish is the only thing on the row in ink. Weight is
   also the only emphasis here that survives a sun-washed phone, which matters
   more than usual on the column that says whether the food is coming — and it is
   never the *only* carrier, because the label is words. */
.q14-line-state {
  flex: 0 1 auto;
  max-width: 34%;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-align: right;
}
/* Struck through *and* labelled. The line-through alone is a visual-only cue and
   the state column already carries the word. */
.q14-line--gone .q14-line-name {
  text-decoration: line-through;
  color: var(--color-text-secondary);
}
.q14-line--gone .q14-line-state {
  color: var(--color-warning);
}

.q14-attribution {
  text-align: center;
}

/*
  The two asks share a row, because they are the same kind of act and neither is
  more important than the other — and because the footer already carries two
  full-width controls under them and a third and fourth stacked would push the
  order's own actions off a small phone.

  It collapses to one column below 320px rather than shrinking the targets: a
  control a wet thumb misses is not a control.
*/
.q14-asks {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-2);
}
@media (max-width: 320px) {
  .q14-asks {
    grid-template-columns: 1fr;
  }
}
.q14-ask-note {
  text-align: center;
}
`;
